import { CreditCard, Trash2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  type PaymentMethod,
  formatCardBrand,
} from '@/hooks/useSubscription';

interface PaymentMethodListProps {
  paymentMethods: PaymentMethod[];
  onRemove?: (paymentMethodId: string) => void;
  onSetDefault?: (paymentMethodId: string) => void;
  isRemoveLoading?: boolean;
  isSetDefaultLoading?: boolean;
  removingId?: string;
  settingDefaultId?: string;
}

/**
 * Displays a list of saved payment methods with options to remove or set as default.
 */
export function PaymentMethodList({
  paymentMethods,
  onRemove,
  onSetDefault,
  isRemoveLoading,
  isSetDefaultLoading,
  removingId,
  settingDefaultId,
}: PaymentMethodListProps) {
  if (paymentMethods.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-muted/50 p-6 text-center">
        <CreditCard className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">
          No payment methods saved
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {paymentMethods.map((pm) => (
        <div
          key={pm.id}
          className="flex items-center justify-between rounded-lg border bg-card p-4"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-btn bg-muted">
              <CreditCard className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {formatCardBrand(pm.card_brand)} ····{pm.card_last4}
                </span>
                {pm.is_primary && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-sapling-primary-100/10 px-2 py-0.5 text-xs font-medium text-sapling-primary-100">
                    <Star className="h-3 w-3" />
                    Default
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Expires {pm.card_exp_month.toString().padStart(2, '0')}/{pm.card_exp_year}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {!pm.is_primary && onSetDefault && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onSetDefault(pm.stripe_payment_method_id)}
                disabled={isSetDefaultLoading || isRemoveLoading}
                loading={settingDefaultId === pm.stripe_payment_method_id && isSetDefaultLoading}
              >
                Set Default
              </Button>
            )}
            {onRemove && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemove(pm.stripe_payment_method_id)}
                disabled={isRemoveLoading || isSetDefaultLoading || pm.is_primary}
                loading={removingId === pm.stripe_payment_method_id && isRemoveLoading}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Compact card display for showing the selected payment method.
 */
export function PaymentMethodDisplay({ paymentMethod }: { paymentMethod: PaymentMethod }) {
  return (
    <div className="flex items-center gap-3 rounded-btn border bg-muted/50 p-3">
      <CreditCard className="h-5 w-5 text-muted-foreground" />
      <div>
        <span className="font-medium">
          {formatCardBrand(paymentMethod.card_brand)} ····{paymentMethod.card_last4}
        </span>
        <span className="ml-2 text-sm text-muted-foreground">
          Expires {paymentMethod.card_exp_month.toString().padStart(2, '0')}/{paymentMethod.card_exp_year}
        </span>
      </div>
    </div>
  );
}
